/**
 * Procurement Pulse — Master Console dashboard for the SAM.gov auto-sync
 * pipeline. Mirrors the visual language of Email Analytics (KPI strip +
 * left-aligned bar charts) so it feels native to the Master Console.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Landmark, Loader2, RefreshCw, TrendingUp, DollarSign,
  Calendar, Bell, ChevronRight, Filter, X,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtInt = (v) => new Intl.NumberFormat("en-US").format(Math.round(v || 0));
const fmtUsd = (v) => `$${new Intl.NumberFormat("en-US").format(Math.round(v || 0))}`;

export default function ProcurementPulse() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  // Filters — left blank means "no filter". Persisted into the request as
  // `naics`, `state`, `agency` query params (the backend match-builder
  // ignores blank strings).
  const [naics, setNaics] = useState("");
  const [state, setState] = useState("");
  const [agency, setAgency] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/master/procurement-pulse/stats`,
        { params: { days, naics, state, agency } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [days, naics, state, agency]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setNaics(""); setState(""); setAgency(""); };
  const activeFilters = [naics, state, agency].filter(Boolean).length;

  return (
    <MasterLayout title="Procurement Pulse">
      <div data-testid="procurement-pulse" className="p-6">
        <header className="flex items-end justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-[26px] font-extrabold text-white flex items-center gap-2">
              <Landmark className="w-6 h-6 text-[#C9A24E]" /> Procurement Pulse
            </h1>
            <p className="text-[12.5px] text-white/55 mt-1">
              SAM.gov Daily Auto-Sync pipeline analytics — opportunities ingested, $ value, and channel performance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              data-testid="pp-days"
              className="h-9 px-3 rounded-lg border bg-white/5 text-white text-[12px]"
              style={{ borderColor: "rgba(201,162,78,0.3)" }}>
              <option value={7} className="bg-[#1A0E36]">last 7 days</option>
              <option value={14} className="bg-[#1A0E36]">last 14 days</option>
              <option value={30} className="bg-[#1A0E36]">last 30 days</option>
              <option value={60} className="bg-[#1A0E36]">last 60 days</option>
              <option value={90} className="bg-[#1A0E36]">last 90 days</option>
            </select>
            <button onClick={load} disabled={loading}
              data-testid="pp-refresh"
              className="h-9 px-3 rounded-lg border text-white text-[12px] inline-flex items-center gap-1.5 hover:bg-white/5"
              style={{ borderColor: "rgba(201,162,78,0.3)" }}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
          </div>
        </header>

        {/* Filter row — NAICS / State / Agency. Empty values are sent through
            and ignored by the backend match-builder. */}
        <div data-testid="pp-filters" className="flex items-center gap-2 flex-wrap mb-5">
          <Filter className="w-3.5 h-3.5 text-[#C9A24E]" />
          <span className="text-[11px] uppercase tracking-wider font-bold text-[#C9A24E]">Filters</span>
          <input
            data-testid="pp-filter-naics" value={naics}
            onChange={(e) => setNaics(e.target.value)}
            placeholder="NAICS (e.g. 541511)"
            className="h-9 px-3 rounded-lg border bg-white/5 text-white text-[12px] w-[160px] focus:outline-none focus:border-[#C9A24E]"
            style={{ borderColor: "rgba(201,162,78,0.3)" }} />
          <input
            data-testid="pp-filter-state" value={state}
            onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="State (CA)" maxLength={2}
            className="h-9 px-3 rounded-lg border bg-white/5 text-white text-[12px] w-[90px] uppercase focus:outline-none focus:border-[#C9A24E]"
            style={{ borderColor: "rgba(201,162,78,0.3)" }} />
          <input
            data-testid="pp-filter-agency" value={agency}
            onChange={(e) => setAgency(e.target.value)}
            placeholder="Agency contains…"
            className="h-9 px-3 rounded-lg border bg-white/5 text-white text-[12px] w-[210px] focus:outline-none focus:border-[#C9A24E]"
            style={{ borderColor: "rgba(201,162,78,0.3)" }} />
          {activeFilters > 0 && (
            <button onClick={clearFilters} data-testid="pp-filters-clear"
              className="h-9 px-3 rounded-lg border text-[11.5px] inline-flex items-center gap-1.5 text-amber-300 hover:bg-white/5"
              style={{ borderColor: "rgba(201,162,78,0.45)" }}>
              <X className="w-3 h-3" /> Clear ({activeFilters})
            </button>
          )}
        </div>

        {loading && !data ? (
          <div className="text-white/55 inline-flex items-center gap-2 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : !data ? null : (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6"
                 data-testid="pp-cards">
              <Kpi label="Total bids" value={fmtInt(data.cards.total)} accent="#3B82F6" />
              <Kpi label="≥ $50k" value={fmtInt(data.cards.high_50k)} accent="#10B981" />
              <Kpi label="≥ $500k" value={fmtInt(data.cards.high_500k)} accent="#F59E0B" />
              <Kpi label="≥ $1M" value={fmtInt(data.cards.high_1m)} accent="#EF4444" />
              <Kpi label="Total value" value={fmtUsd(data.cards.total_value)} accent="#C9A24E" wide />
              <Kpi label="Avg value" value={fmtUsd(data.cards.avg_value)} accent="#A855F7" />
            </div>

            {/* Last cron run + recent digests */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
              <Card title="Last cron run" icon={Calendar}>
                {data.last_run?.finished_at ? (
                  <div className="space-y-1 text-[12.5px]">
                    <Row k="Finished" v={new Date(data.last_run.finished_at).toLocaleString()} />
                    <Row k="Trigger" v={data.last_run.triggered_by || "—"} />
                    <Row k="Inserted" v={fmtInt(data.last_run.inserted)} accent="#10B981" />
                    <Row k="Updated" v={fmtInt(data.last_run.updated)} accent="#3B82F6" />
                    <Row k="Slack alerts" v={fmtInt(data.last_run.alerts_sent)} accent="#F59E0B" />
                  </div>
                ) : (
                  <p className="text-[12.5px] text-white/55">No cron has run yet. Enable in Procurement Scanner → Sources.</p>
                )}
              </Card>
              <Card title="Recent Slack digests" icon={Bell}>
                {(data.recent_digests || []).length === 0 ? (
                  <p className="text-[12.5px] text-white/55">No digests sent yet. They fire only when the cron finds bids above the threshold.</p>
                ) : (
                  <ul className="space-y-1.5 text-[12px]">
                    {data.recent_digests.map((d, i) => (
                      <li key={i} className="flex items-center justify-between border-b border-white/10 last:border-0 pb-1.5">
                        <span className="font-mono-tech text-white/65">{new Date(d.sent_at).toLocaleString()}</span>
                        <span><b className="text-amber-400">{d.count}</b> bids · <b>{fmtUsd(d.total_value)}</b> total</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {/* Daily chart */}
            <Card title="Bids per day" icon={TrendingUp} className="mb-6">
              <DailyChart series={data.daily} />
            </Card>

            {/* Top dimensions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <Card title="Top NAICS (by $)" icon={DollarSign}>
                <BarList rows={data.top_naics} valueKey="value" valueFmt={fmtUsd} />
              </Card>
              <Card title="Top States" icon={ChevronRight}>
                <BarList rows={data.top_states} valueKey="n" valueFmt={fmtInt} />
              </Card>
              <Card title="Top Agencies" icon={ChevronRight}>
                <BarList rows={data.top_agencies} valueKey="n" valueFmt={fmtInt} />
              </Card>
            </div>
          </>
        )}
      </div>
    </MasterLayout>
  );
}

function Kpi({ label, value, accent, wide }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${wide ? "col-span-2 md:col-span-1" : ""}`}
         style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#C9A24E" }}>{label}</div>
      <div className="text-[22px] font-extrabold mt-1 leading-none" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Card({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`rounded-2xl border p-4 ${className}`}
         style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
      <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#C9A24E] mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/55">{k}</span>
      <span className="font-bold" style={{ color: accent || "white" }}>{v}</span>
    </div>
  );
}

function BarList({ rows, valueKey, valueFmt }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  if (!rows || rows.length === 0)
    return <p className="text-[12px] text-white/55">No data.</p>;
  return (
    <div className="space-y-1.5">
      {rows.slice(0, 8).map((r, i) => (
        <div key={`${r.k}-${i}`} className="flex items-center gap-2 text-[11.5px]">
          <span className="truncate w-32 shrink-0 font-mono-tech text-white/80">{r.k || "—"}</span>
          <div className="flex-1 bg-white/10 rounded h-2 overflow-hidden">
            <div className="h-full rounded transition-all"
                 style={{ width: `${((r[valueKey] || 0) / max) * 100}%`, background: "#C9A24E" }} />
          </div>
          <span className="font-bold w-20 text-right text-white tabular-nums">{valueFmt(r[valueKey])}</span>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ series }) {
  if (!series || series.length === 0)
    return <p className="text-[12px] text-white/55">No data yet — run the cron once.</p>;
  const maxCount = Math.max(1, ...series.map((d) => d.count));
  const W = 880, H = 140, pad = 24;
  const xStep = (W - 2 * pad) / Math.max(1, series.length - 1);
  const path = series.map((d, i) => {
    const x = pad + i * xStep;
    const y = H - pad - ((d.count / maxCount) * (H - 2 * pad));
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
        <path d={path} fill="none" stroke="#C9A24E" strokeWidth="2" />
        {series.map((d, i) => {
          const x = pad + i * xStep;
          const y = H - pad - ((d.count / maxCount) * (H - 2 * pad));
          return <circle key={i} cx={x} cy={y} r="3" fill="#C9A24E" />;
        })}
      </svg>
      <div className="flex justify-between text-[10.5px] text-white/45 font-mono-tech mt-1">
        <span>{series[0]?.day}</span>
        <span>peak: {maxCount}/day</span>
        <span>{series.at(-1)?.day}</span>
      </div>
    </div>
  );
}
